import React, { useContext, useState, useRef, useEffect } from 'react';

// Util
import uuid from 'uuid';
import { useParams } from 'react-router';
import useProjectStore from '../../../../../ProjectCanvas/useProjectStore';
import { createSaveEvent } from '../../../../../../utils/createSaveEvent';
import { ComponentFactory } from '../../../../../ProjectCanvas/ComponentRegistry';
import { send_request } from '../../../../../../utils/Request';
import { isLegacyForm } from '../../../../../../utils/formTypeConstants';
import { saveCanvasEvent } from '../../../../../../utils/canvasApi';
import { initializeComponent } from '../../../../../../utils/ComponentInitializationUtil';
import { getColourFromString } from '../../../../../../utils/ColourNameGenerator';
import BaseComponent from './BaseComponent';

// Canvas
import { CanvasContext } from '../../../WorkflowCanvas';
import { getElements, calculateNewItemPostion } from '../../../../../../utils/CanvasUtil';

// Custom components
import { CustomDialog } from '../../../../../Component/Dialog';
import ConvertFormDialog from '../../../Dialogs/ConvertFormDialog';
import ComponentSettingsToolbar from './ComponentNodeUtil/ComponentSettingsToolbar';

// MUI
import { Button, Grid, Select, MenuItem, Autocomplete, TextField } from '@mui/material';
import { withStyles } from '@mui/styles';

// Icons
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ScienceIcon from '@mui/icons-material/Science';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle';
import DeleteIcon from '@mui/icons-material/Delete';

// Zustand
import useCanvasStore from '../../../useCanvasStore';

// Inline editing

import InlineConfigPanel from '../../InlineConfigPanel';
import InlineNodeView from './InlineNodeView';

// Styles
import { styles } from './ComponentNodeUtil/CustomComponentNodeUtil';

const DraftCanvasComponent = ({ id, data, isConnectable, classes, selected, xPos, yPos }) => {
    const {
      modeInfo,
      isChildTemplateLoaded,
      isCanvasLocked,
      edit,
      testPanel,
      canvasInstance,
      savingInfo,
      openComponentAndTestPanel,
      updated,
      loadDialog,
      unpublishedChanges,
      allPlaceholdersInWorkflow,
      isOutputPlaceholdersLoading,
      lastNodeClickTime,
    } = useContext(CanvasContext);
    const [childTemplateLoaded, setChildTemplateLoaded] = isChildTemplateLoaded || [];
    const [canvasLocked] = isCanvasLocked || [false];
    if (modeInfo === undefined) {
      return <BaseComponent classes={classes} data={data} isConnectable={isConnectable} />;
    }
    const parentRef = useRef(null);
    const [height, setHeight] = useState(0);
    const ref = useRef(null);
    useEffect(() => {
      if (ref.current) {
        const newHeight = ref.current.clientHeight;
        setHeight(newHeight);
      }
    }, []);

    const [mode, setMode] = modeInfo;

    const nodes = useCanvasStore(s => s.nodes);
    const edges = useCanvasStore(s => s.edges);

    const [editComponent, setEditComponent] = edit ? edit : useState({});

    const [saving, setSaving] = savingInfo ? savingInfo : useState({});
    const [reactFlowInstance, setReactFlowInstance] = canvasInstance
      ? canvasInstance
      : useState({});
    // dialog flags
    const [deleteNode, setDeleteNode] = useState(false);
    const [copyNode, setCopyNode] = useState(false);
    const [workflowType, setWorkflowType] = useState('current_workflow');
    const [workflows, setWorkflows] = useState([]);
    const [workflow, setWorkflow] = useState('');
    const [moreComponents, setMoreComponents] = useState(false);
    const [loadCancel, setLoadCancel] = useState(false);
    const [loadCopy, setLoadCopy] = useState(false);
    const [node, setNode] = useState([]);

    //form transform flags
    const [openConvertForm, setOpenConvertForm] = useState(false);
    const [convertTo, setConvertTo] = useState({});

    //Selected components
    const [isSelectedComponent, setIsSelectedComponent] = useState(false);

    const [svgSelectedClass, setSvgSelectedClass] = useState('');

    const [isHovered, setIsHovered] = useState(false);

    //record last modified value for the canvas
    const [lastModified, setLastModified] = updated ? updated : useState({});

    const [openTestPanel, setOpenTestPanel] = testPanel ? testPanel : useState({});

    const [loadingDialog, setLoadingDialog] = loadDialog ? loadDialog : useState(false);
    const [checkUnpublishedChanges] = unpublishedChanges ? unpublishedChanges : '';

    //react-router use params
    const params = useParams();

    let projectId = params.id;
    const componentIdForOutputs = data?.componentId || id;
    const outputPlaceholders = allPlaceholdersInWorkflow?.[componentIdForOutputs] || [];

    const handleDelete = async () => {
      if (saving) return;

      setSaving(true);
      let result = [...nodes, ...edges];

      if (data.type === 'conditional_workflow_path') {
        // Delete next components on canvas
        const nextCompToDelete = data.next;
        if (Array.isArray(nextCompToDelete)) {
          const componentIdsToDelete = nextCompToDelete.map(item => item.componentId);
          result = result.filter(
            node =>
              !(
                componentIdsToDelete.includes(node.id) &&
                node.data.type === 'conditional_workflow' &&
                node.data.fromWorkflowPath === true
              )
          );
        }
      }

      result = result.filter(node => {
        return node.id !== id;
      });

      let oldDbid = useProjectStore.getState().dbIds.get(id);
      if (oldDbid !== undefined) {
        useProjectStore.getState().dbIds.delete(id);

        if (new Set(useProjectStore.getState().dbIds.values()).has(oldDbid)) {
          if (id.displayName === 'Query Database') {
            result = result.filter(node => {
              return !(node.target === id.componentId && node.source === oldDbid);
            });
          } else {
            result = result.filter(node => {
              return !(node.source === id.componentId && node.target === oldDbid);
            });
          }
        } else {
          result = result.filter(node => {
            return node.id !== oldDbid;
          });
        }
      }

      useCanvasStore.getState().setNodesAndEdges(result);

      let rfiObj = reactFlowInstance.toObject();

      let viewport = {
        x: rfiObj.viewport.x.toFixed(5),
        y: rfiObj.viewport.y.toFixed(5),
        zoom: rfiObj.viewport.zoom.toFixed(5),
      };

      let eventData = {
        componentId: id,
        type: data.type,
      };

      let saveEvent = createSaveEvent(
        'DELETE_COMPONENT',
        useProjectStore.getState().project_id,
        viewport,
        getElements(reactFlowInstance),
        eventData
      );

      //send event
      await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent)
        .then(response => {
          if (response.data && response.data.updated) {
            setLastModified(response.data.lastModified);
          }
          //insert delay here
          setSaving(false);
        })
        .catch(error => {
          console.log(error);
        });
    };
    //copy dialog requests
    const handleSelectWorkflowType = async value => {
      setWorkflowType(value);
      setLoadCopy(true);

      if (value === 'another_workflow' && workflows.length === 0) {
        const json = await send_request(
          `project-service/project/get_workflow_names/${projectId}`
        ).catch(e => {
          console.log(e);
          closeCopy();
        });

        if (json && json.status === 200) {
          setWorkflows(json.data);
        }
      } else setLoadCopy(false);
    };

    const handleSelectWorkflow = async input => {
      if (input === 'select_workflow' && workflowType === 'another_workflow') setLoadCopy(true);
      else setLoadCopy(false);

      setWorkflow(input);
    };

    const closeCopy = () => {
      setWorkflow('select_workflow');
      setWorkflowType('current_workflow');
      setNode({});
      setCopyNode(false);
      setWorkflows([]);
      setLoadCopy(false);
      setLoadCancel(false);
    };

    const openInNewTab = () => {
      if (editComponent)
        //if component pane is open
        setEditComponent(null);

      //Redirect to DraftCanvasComponent Page Url
      let url = window.location.pathname + `/${data.type}/${id}`;
      window.open(url, '_blank');
    };

    const requestCopy = async () => {
      if (nodes.length < 250) {
        setLoadCopy(true);
        setLoadCancel(true);

        let copyToId = '';

        if (workflowType === 'current_workflow') {
          copyToId = projectId;
        } else {
          copyToId = workflow.projectId;
        }

        let eventData = {
          projectId: copyToId,
          componentId: id,
        };

        let rfiObj = reactFlowInstance.toObject();

        let viewport = {
          x: rfiObj.viewport.x.toFixed(5),
          y: rfiObj.viewport.y.toFixed(5),
          zoom: rfiObj.viewport.zoom.toFixed(5),
        };

        let saveEvent = createSaveEvent(
          'COPY_COMPONENT',
          useProjectStore.getState().project_id,
          viewport,
          getElements(reactFlowInstance),
          eventData
        );

        //send event
        const json = await saveCanvasEvent(useProjectStore.getState().project_id, saveEvent).catch(e => {
          console.log(e);
          closeCopy();
        });

        //handle updating canvas if we create a new component
        if (
          json &&
          json.status === 200 &&
          (workflowType === 'current_workflow' || workflow === projectId)
        ) {
          console.log(json.data);
          let component = json.data.copiedComponent;
          let template = ComponentFactory[component.type];

          let nodesAndEdgesResult = [...nodes, ...edges];

          const newNode = {
            id: component.componentId,
            type: 'component',
            position: component.viewData,
            data: {
              instruction: component.instruction ? component.instruction : '',
              type: component.type,
              label: component.componentData?.name,
              description: component.description,
              displayName: template.displayName,
              logo: template.logo,
              hasInput: template.hasInput ? template.hasInput : false,
              hasOutput: template.hasOutput ? template.hasOutput : false,
              baseColor: template.baseColor,
            },
          };

          // Check if the copy component has a database connection
          if (useProjectStore.getState().dbIds.has(id)) {
            useProjectStore.getState().createDbNodeLogic(
              component,
              useProjectStore.getState().dbIds,
              nodesAndEdgesResult,
              useProjectStore.getState().dbIds.get(id),
              newNode.data
            );
            newNode.data.hasDb = true;
          }

          nodesAndEdgesResult.push(newNode);

          // Set the nodes and edges
          useCanvasStore.getState().setNodesAndEdges(nodesAndEdgesResult);
        }
      } else setMoreComponents(true);

      closeCopy();
    };

    // Convert component only applies to legacy form types (pages schema)
    const isForm = isLegacyForm;

    const handleConvertTo = value => {
      setConvertTo(value);
    };

    const handleComponentConverted = (newType, changeTo, componentId) => {
      const updatedNodes = nodes.map(component => {
        if (component.id === componentId) {
          return {
            ...component,
            data: {
              ...component.data,
              type: changeTo.name,
              logo: changeTo.logo,
              baseColor: changeTo.baseColor,
              hasInput: changeTo.hasInput,
              hasOutput: changeTo.hasOutput,
              displayName: changeTo.displayName,
            },
          };
        }
        return component;
      });

      useCanvasStore.getState().setNodes(updatedNodes);
    };

    useEffect(() => {
      const dataComponentId = data?.componentId || id;
      const isEditComponentSelected =
        editComponent && dataComponentId === editComponent.componentId;
      const isTestPanelOpen = openTestPanel && data.componentId === openTestPanel;

      setIsSelectedComponent(isEditComponentSelected || isTestPanelOpen);
    }, [editComponent, openTestPanel, data, id]);

    useEffect(() => {
      const isComponentSelected = selected || isSelectedComponent;
      setSvgSelectedClass(isComponentSelected ? classes.selectedIcon : '');
    }, [isSelectedComponent, selected, classes.selectedIcon]);

    const addLinkedConditionalPath = async data => {
      if (saving) return;

      setSaving(true);

      const newComponentType = 'conditional_workflow';
      const newPathId = uuid();

      const template = ComponentFactory[newComponentType];

      // Create new path component
      const newComponentPosition = calculateNewItemPostion(
        xPos,
        yPos,
        120,
        120,
        200,
        100,
        getElements(reactFlowInstance)
      );

      const newComponent = {
        id: newPathId,
        type: 'component',
        data: {
          logo: template.logo,
          hasInput: template.hasInput ? template.hasInput : false,
          hasOutput: template.hasOutput ? template.hasOutput : false,
          type: newComponentType,
          displayName: template.displayName,
          next: [],
          label: template.displayName,
          baseColor: template.baseColor,
          color: getColourFromString(),
        },
        position: newComponentPosition,
      };

      // Create new link from branch path to the new path component
      const newLink = {
        id: uuid(),
        source: id,
        target: newPathId,
        markerEnd: {
          type: 'arrowclosed',
          color: '#868686',
        },
        type: 'link',
        label: 'componentLink',
        data: { showDeleteButton: false },
        style: {
          strokeWidth: '3px',
          stroke: data.baseColor,
        },
      };

      // Add it to the canvas
      useCanvasStore.getState().setNodes([...nodes, newComponent]);
      useCanvasStore.getState().setEdges([...edges, newLink]);

      // Perform save on the backend
      let rfiObj = reactFlowInstance.toObject();

      let viewport = {
        x: rfiObj.viewport.x.toFixed(5),
        y: rfiObj.viewport.y.toFixed(5),
        zoom: rfiObj.viewport.zoom.toFixed(5),
      };

      let eventData = {
        sourceComponentId: id, // source is the branch_paths
        targetComponentId: newPathId,
        targetType: newComponentType,
        componentId: newPathId,
        type: newComponentType,
        viewData: newComponentPosition,
      };

      let saveEvent = createSaveEvent(
        'ADD_COMPONENT_AND_LINK',
        projectId,
        viewport,
        getElements(reactFlowInstance),
        eventData
      );

      // Send event to save
      return await saveCanvasEvent(projectId, saveEvent)
        .then(response => {
          if (response.data && response.data.updated) {
            setLastModified(response.data.lastModified);
            initializeComponent(newComponentType, newPathId);
          }
          setSaving(false);
        })
        .catch(error => {
          console.log(error);
        });
    };

    // Check if this component is the source of a loop edge
    const hasLoop = edges.some(edge => edge.type === 'loopLink' && edge.source === id);

    const nodeSettingsMenuItems = [
      {
        title: 'Run in production',
        icon: <PlayCircleIcon />,
        text: 'Run',
        onClick: () => {
          setLoadingDialog(true);
          checkUnpublishedChanges(true, id);
        },
        condition: !data.isTemp && !data.fromWorkflowPath,
      },
      {
        title: 'Start test run',
        icon: <ScienceIcon />,
        text: 'Test',
        onClick: () => openComponentAndTestPanel(id),
        condition: !data.fromWorkflowPath,
      },
      {
        title: 'Copy',
        icon: <FileCopyIcon />,
        onClick: () => setCopyNode(true),
        condition: !data.fromWorkflowPath,
      },
      {
        title: 'Convert component',
        icon: <SwapHorizontalCircleIcon />,
        onClick: () => setOpenConvertForm(true),
        condition: isForm(data.type) && !data.isTemp && !data.fromWorkflowPath,
      },
      {
        title: 'Delete',
        icon: <DeleteIcon />,
        onClick: () => setDeleteNode(true),
      },
    ].filter(action => action.condition !== false);

    useEffect(() => {
      const handleBeforeUnload = e => {
        e.preventDefault();
        e.returnValue = '';
        return '';
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, []);

    return (
      <>
        <div
          draggable={false}
          ref={parentRef}
          onDragOver={event => {
            event.preventDefault();
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ width: 520, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
              <InlineNodeView
                data={data}
                nodeId={id}
                saving={saving}
                isHovered={isHovered}
                addLinkedConditionalPath={addLinkedConditionalPath}
                currentXPosition={xPos}
                currentYPosition={yPos}
                selected={selected}
              >
                <InlineConfigPanel
                  componentId={id}
                  componentType={data.type}
                  projectId={projectId}
                  version={null}
                  nodeData={data}
                />
              </InlineNodeView>
              <ComponentSettingsToolbar
                selected={selected || isHovered}
                classes={classes}
                data={data}
                actions={nodeSettingsMenuItems}
                enableWorkflowPath={true}
                canvasLocked={canvasLocked}
              />
        </div>

        {/* Dialogs */}
        <CustomDialog
          isOpen={deleteNode}
          size={'sm'}
          title={<h5> Confirm Delete </h5>}
          contents={
            <>
              <p className={' s-text'}>
                Are you sure you want to delete this component ? All data and settings for this
                component will be lost and this action cannot be undone.
              </p>
            </>
          }
          buttons={
            <>
              <Button onClick={handleDelete} variant="outlined" disabled={saving}>
                Delete
              </Button>
              <Button onClick={() => setDeleteNode(false)} variant="contained" color="primary">
                Cancel
              </Button>
            </>
          }
        />
        <CustomDialog
          isOpen={moreComponents}
          title={'You have reached the maximum of 250 components for this canvas.'}
          buttons={
            <Button
              onClick={() => {
                setMoreComponents(false);
              }}
              variant="contained"
              color="primary"
            >
              Close
            </Button>
          }
        />
        <ConvertFormDialog
          component={data}
          componentId={id}
          open={openConvertForm}
          convertTo={convertTo}
          handleChange={value => handleConvertTo(value)}
          projectId={projectId}
          handleComponentUpdated={(componentType, changeTo) => {
            handleComponentConverted(componentType, changeTo, id);
          }}
          handleClose={() => {
            setOpenConvertForm(false);
          }}
        />
        <CustomDialog
          isOpen={copyNode}
          title={'Copy and paste component'}
          contents={
            <Grid>
              <p className={'s-text'}>Create a copy of this component and paste it in:</p>
              <Select
                value={workflowType}
                fullWidth={true}
                disableUnderline={true}
                variant={'filled'}
                onChange={event => handleSelectWorkflowType(event.target.value)}
              >
                <MenuItem value={'current_workflow'}>Current workflow</MenuItem>
                <MenuItem value={'another_workflow'}>Another workflow</MenuItem>
              </Select>

              {workflowType === 'another_workflow' && (
                <Autocomplete
                  freeSolo
                  size="small"
                  loading={workflows.length <= 0}
                  options={workflows}
                  getOptionLabel={option =>
                    option.projectName
                      ? option.projectName
                      : option.projectId
                      ? option.projectId
                      : ''
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.projectId}>
                      {option.projectName ? option.projectName : option.projectId}
                    </li>
                  )}
                  renderInput={params => (
                    <TextField {...params} margin="normal" variant="outlined" />
                  )}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleSelectWorkflow(newValue);
                    }
                  }}
                  value={''}
                  className={'copySelect'}
                />
              )}
            </Grid>
          }
          buttons={
            <>
              <Button variant="outlined" disabled={loadCancel} onClick={() => setCopyNode(false)}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" disabled={loadCopy} onClick={requestCopy}>
                Done
              </Button>
            </>
          }
        />
      </>
    );
};

export default withStyles(styles)(DraftCanvasComponent);
